import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import './EntitateClusterBubbles.css';

/**
 * Country-zoom cluster bubbles for /app/filter.
 *
 * Each `entitateSilvica` aggregates into a single circular badge
 * showing parcel and photo counts. Below the zoom threshold the
 * bubbles are visible and the real parcel / photo layers are
 * hidden — gives users an instant "where's the activity?" overview
 * without painting 3,500 polygons at country scale. Click a bubble
 * to flyToBounds onto that entitate's bbox; the zoom-in trigger
 * naturally swaps in the detailed layers.
 */

export type ClusterBubble = {
  entitateSilvica: string | null;
  parcelCount: number;
  photoCount: number;
  totalAreaHa: number;
  centroid: [number, number];
  bbox: [number, number, number, number];
};

type ClustersResponse = {
  version: number;
  entities: ClusterBubble[];
};

/** Same filter shape as the parcel list query. Anything truthy is
 *  forwarded to the API; empty / undefined fields are skipped so a
 *  no-filter page hits the cached aggregator on the server. */
export type ClusterBubbleFilters = {
  search?: string;
  raioane?: string;
  primarii?: string;
  ocoaleSilvice?: string;
  entitatiSilvice?: string;
  years?: string;
  anulMin?: number;
  anulMax?: number;
  codProiect?: string;
  ucf?: string;
  suprafataMin?: number;
  suprafataMax?: number;
  // Photo-only filters. Without these a filter like "author = X" left
  // every bubble untouched while the table below showed three rows.
  semester?: number;
  polygonStatuses?: string;
  hasParcel?: boolean;
  project?: string;
  month?: number;
  authorCods?: string;
};

function filtersToSearchParams(f: ClusterBubbleFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.search) p.set('search', f.search);
  if (f.raioane !== undefined) p.set('raioane', f.raioane);
  if (f.primarii !== undefined) p.set('primarii', f.primarii);
  if (f.ocoaleSilvice !== undefined) p.set('ocoaleSilvice', f.ocoaleSilvice);
  if (f.entitatiSilvice !== undefined) p.set('entitatiSilvice', f.entitatiSilvice);
  if (f.years !== undefined) p.set('years', f.years);
  if (f.anulMin !== undefined) p.set('anulMin', String(f.anulMin));
  if (f.anulMax !== undefined) p.set('anulMax', String(f.anulMax));
  if (f.codProiect) p.set('codProiect', f.codProiect);
  if (f.ucf) p.set('ucf', f.ucf);
  if (f.suprafataMin !== undefined) p.set('suprafataMin', String(f.suprafataMin));
  if (f.suprafataMax !== undefined) p.set('suprafataMax', String(f.suprafataMax));
  if (f.semester !== undefined) p.set('semester', String(f.semester));
  if (f.polygonStatuses !== undefined) p.set('polygonStatuses', f.polygonStatuses);
  if (f.hasParcel !== undefined) p.set('hasParcel', String(f.hasParcel));
  if (f.project) p.set('project', f.project);
  if (f.month !== undefined) p.set('month', String(f.month));
  if (f.authorCods !== undefined) p.set('authorCods', f.authorCods);
  return p;
}

function useClusterBubbles(filters: ClusterBubbleFilters) {
  return useQuery<ClustersResponse>({
    queryKey: ['parcels', 'cluster-bubbles', filters],
    queryFn: async () =>
      api
        .get('parcels/cluster-bubbles', { searchParams: filtersToSearchParams(filters) })
        .json<ClustersResponse>(),
    staleTime: 60_000,
  });
}

type Props = {
  /** When zoom < this, bubbles are visible; otherwise hidden. Default 9. */
  threshold?: number;
  /** Active filters from FilterPage. Bubble counts narrow accordingly. */
  filters?: ClusterBubbleFilters;
};

/** Diameter bounds, in screen pixels. Small on purpose: a bubble is a
 *  marker for its territory, not a heat blob covering it. */
export const MIN_SIZE = 16;
export const MAX_SIZE = 34;
/** Share of the entity's on-screen bbox a bubble may occupy. Keeps the
 *  circle inside the territory it counts instead of spilling over the
 *  neighbours. */
const BBOX_FILL = 0.55;

/**
 * Bubble size scales with parcel count so dense entities anchor the
 * visual layout. Capped so very large entities don't dominate.
 */
export function bubbleSize(parcelCount: number): number {
  // Log scale keeps the spread perceptible without letting the largest
  // entity dwarf everything (a plain area/count scale would).
  const scale = Math.min(1, Math.log10(parcelCount + 1) / 2.8);
  return Math.round(MIN_SIZE + (MAX_SIZE - MIN_SIZE) * scale);
}

/**
 * Largest diameter that still sits inside the entity's own footprint at
 * the current zoom. Uses the shorter bbox side, so a long thin territory
 * doesn't get a bubble wider than it is tall.
 */
export function bboxFitSize(map: L.Map, bbox: [number, number, number, number]): number {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const sw = map.latLngToLayerPoint([minLat, minLon]);
  const ne = map.latLngToLayerPoint([maxLat, maxLon]);
  return Math.min(Math.abs(ne.x - sw.x), Math.abs(sw.y - ne.y)) * BBOX_FILL;
}

/** Clear space kept between two circles, in pixels. */
const BUBBLE_GAP = 3;
/** How far a bubble may drift off its centroid to dodge a neighbour.
 *  Small enough that it still reads as belonging to its territory. */
const MAX_NUDGE = 20;

export type BubblePlacement = {
  /** Diameter after shrinking to avoid neighbours. */
  size: number;
  /** Screen-space offset from the entity centroid, in pixels. */
  dx: number;
  dy: number;
};

type Circle = { x: number; y: number; r: number };

function overlapping(c: Circle, placed: readonly Circle[]): Circle | null {
  for (const p of placed) {
    const dist = Math.hypot(c.x - p.x, c.y - p.y);
    if (dist < c.r + p.r) return p;
  }
  return null;
}

/**
 * Lay the bubbles out so no two circles touch.
 *
 * Entities are placed densest-first, so the ones that matter most keep
 * their size and position; each later bubble first **shrinks** (down to
 * the legibility floor) and then **nudges** away from whatever it still
 * hits, up to `MAX_NUDGE`. A bubble that cannot be separated even then
 * is kept rather than dropped — losing an entity from the overview
 * would be worse than a rare residual touch.
 *
 * Positions come from `latLngToLayerPoint`, which is stable under
 * panning, so this only has to re-run when the zoom changes.
 */
export function resolvePlacements(
  map: L.Map,
  entities: readonly ClusterBubble[],
  baseSize: (e: ClusterBubble) => number,
): Map<ClusterBubble, BubblePlacement> {
  const out = new Map<ClusterBubble, BubblePlacement>();
  const placed: Circle[] = [];
  const byImportance = [...entities].sort((a, b) => b.parcelCount - a.parcelCount);

  for (const e of byImportance) {
    const p = map.latLngToLayerPoint([e.centroid[1], e.centroid[0]]);
    let size = baseSize(e);
    let x = p.x;
    let y = p.y;

    // 1) Give way by getting smaller before moving. `Math.max` keeps
    //    MIN_SIZE a true floor — a bare `size -= 2` from an odd base
    //    steps straight past it.
    while (size > MIN_SIZE && overlapping({ x, y, r: size / 2 + BUBBLE_GAP }, placed)) {
      size = Math.max(MIN_SIZE, size - 2);
    }

    // 2) Still colliding: push directly away from the blocker, re-testing
    //    after each step (a push can walk into a third bubble).
    for (let step = 0; step < 12; step++) {
      const hit = overlapping({ x, y, r: size / 2 + BUBBLE_GAP }, placed);
      if (!hit) break;
      const dist = Math.hypot(x - hit.x, y - hit.y) || 0.001;
      const need = hit.r + size / 2 + BUBBLE_GAP - dist;
      // Deterministic direction when two centroids coincide exactly.
      const ux = dist < 0.01 ? 1 : (x - hit.x) / dist;
      const uy = dist < 0.01 ? 0 : (y - hit.y) / dist;
      const nx = x + ux * need;
      const ny = y + uy * need;
      const drift = Math.hypot(nx - p.x, ny - p.y);
      if (drift > MAX_NUDGE) {
        // Out of drift budget. Spend what is left rather than giving up:
        // partial separation still beats two circles dead on top of each
        // other, which is what identical centroids would otherwise give.
        const t = MAX_NUDGE / drift;
        x = p.x + (nx - p.x) * t;
        y = p.y + (ny - p.y) * t;
        break;
      }
      x = nx;
      y = ny;
    }

    placed.push({ x, y, r: size / 2 + BUBBLE_GAP });
    out.set(e, { size, dx: x - p.x, dy: y - p.y });
  }
  return out;
}

/** Tracks the current zoom so we can show/hide the layer reactively. */
function useMapZoom(): number {
  const map = useMap();
  const [zoom, setZoom] = useState<number>(() => map.getZoom());
  useEffect(() => {
    const onZoom = () => setZoom(map.getZoom());
    map.on('zoomend', onZoom);
    return () => {
      map.off('zoomend', onZoom);
    };
  }, [map]);
  return zoom;
}

const EMPTY_FILTERS: ClusterBubbleFilters = {};

export function EntitateClusterBubbles({
  threshold = 9,
  filters = EMPTY_FILTERS,
}: Props) {
  const map = useMap();
  const zoom = useMapZoom();
  const { data } = useClusterBubbles(filters);
  const markersRef = useRef<L.Marker[]>([]);

  const visible = zoom < threshold;
  const entities = useMemo(() => data?.entities ?? [], [data]);

  useEffect(() => {
    if (!map) return;
    // Clear previous markers
    for (const m of markersRef.current) {
      map.removeLayer(m);
    }
    markersRef.current = [];
    if (!visible || entities.length === 0) return;

    // Sizes and offsets are resolved together so no two circles touch.
    const placements = resolvePlacements(map, entities, (e) =>
      // Whichever is smaller: the count-derived size, or what fits in the
      // territory. MIN_SIZE still wins over a tiny footprint — a dot too
      // small to read its own number would be useless.
      Math.max(
        MIN_SIZE,
        Math.min(bubbleSize(e.parcelCount), Math.round(bboxFitSize(map, e.bbox))),
      ),
    );

    // Render smaller bubbles last so denser entities sit on top and stay
    // clickable wherever the captions still crowd each other.
    const ordered = [...entities].sort((a, b) => a.parcelCount - b.parcelCount);
    for (const e of ordered) {
      const placement = placements.get(e);
      if (!placement) continue;
      const { size, dx, dy } = placement;
      const label = e.entitateSilvica ?? '—';
      // Big count inside the circle; the entity name reads as a pill BELOW
      // the circle (never truncated mid-glyph inside it); photos as a badge.
      // Font follows the digit count, not just the diameter: at the floor
      // size a 3-digit count set at 0.4em would run past the circle's edge.
      const digits = String(e.parcelCount).length;
      const countPx = Math.min(
        Math.round(size * 0.5),
        Math.max(7, Math.round((size * 0.9) / Math.max(2, digits))),
      );
      // Photo badge carries the count only — the camera glyph nearly
      // doubled its width, so on a ~30px circle the badge rivalled the
      // bubble it hangs off. Blue pill vs green circle already separates
      // photos from polygons, and the tooltip spells both out.
      const html = `
        <div class="cb-root" style="width:${size}px;height:${size}px;">
          ${e.photoCount > 0 ? `<span class="cb-badge">${e.photoCount}</span>` : ''}
          <div class="cluster-bubble" style="width:${size}px;height:${size}px;">
            <span class="cb-count" style="font-size:${countPx}px;">${e.parcelCount}</span>
          </div>
          <span class="cb-caption">${escapeHtml(label)}</span>
        </div>
      `;
      const icon = L.divIcon({
        className: 'cluster-bubble-wrap',
        html,
        iconSize: [size, size],
        // The anchor carries the de-collision nudge: the marker keeps its
        // real centroid (so click-to-zoom still targets the right place)
        // while the icon draws a few pixels off it.
        iconAnchor: [size / 2 - dx, size / 2 - dy],
      });
      const marker = L.marker([e.centroid[1], e.centroid[0]], {
        icon,
        interactive: true,
        keyboard: false,
        riseOnHover: true,
      });
      marker.bindTooltip(
        `<strong>${escapeHtml(label)}</strong><br/>${e.parcelCount} poligoane · ${e.photoCount} foto · ${e.totalAreaHa.toLocaleString('ro-MD', { maximumFractionDigits: 1 })} ha`,
        { direction: 'top', opacity: 0.95 },
      );
      marker.on('click', () => {
        const [minLon, minLat, maxLon, maxLat] = e.bbox;
        // maxZoom 13: deep enough that polygons are clearly clickable
        // and the UCF labels are large; without this, large entitati
        // would land at z9-10 where individual parcels are 3-5 px wide.
        map.flyToBounds(
          [
            [minLat, minLon],
            [maxLat, maxLon],
          ],
          { padding: [40, 40], maxZoom: 13, duration: 0.6 },
        );
      });
      marker.addTo(map);
      markersRef.current.push(marker);
    }

    return () => {
      for (const m of markersRef.current) map.removeLayer(m);
      markersRef.current = [];
    };
    // `zoom` is a dependency because the fit-to-territory cap is measured
    // in screen pixels: the same bbox is worth more pixels as you zoom in.
  }, [map, entities, visible, zoom]);

  return null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
